'use client';

import React, { useState } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { QuickCalculatorForm } from '@/components/quick-calculator/QuickCalculatorForm';
import { calculateTransitChart, TransitChartResult, CreateBirthProfileInput } from '@/lib/api/client';
import { Compass, Sparkles, Orbit } from 'lucide-react';
import { formatLocalizedDate } from '@/lib/i18n/dateFormat';

export interface TransitChartCalculatorClientProps {
  initialResult?: TransitChartResult | null;
  initialError?: string | null;
}

export function TransitChartCalculatorClient({
  initialResult = null,
  initialError = null
}: TransitChartCalculatorClientProps) {
  const t = useTranslations('transitChart');
  const tGlossary = useTranslations('glossary');
  const locale = useLocale();

  const [result, setResult] = useState<TransitChartResult | null>(initialResult);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(initialError);
  const [transitDate, setTransitDate] = useState<string>('');

  const handleCalculate = async (input: CreateBirthProfileInput) => {
    setLoading(true);
    setError(null);
    try {
      const data = await calculateTransitChart(input, transitDate || undefined);
      setResult(data);
    } catch (err: any) {
      setError(err.message || t('calculateBtn'));
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const getPlanetName = (planetKey: string): string => {
    const key = planetKey.toLowerCase();
    try {
      const val = tGlossary(`planets.${key}` as any);
      if (val && !val.startsWith('planets.')) return val;
    } catch (_) {}
    return planetKey;
  };

  const getSignName = (signKey?: string | null): string => {
    if (!signKey) return '';
    const key = signKey.toLowerCase();
    try {
      const val = tGlossary(`zodiacSigns.${key}` as any);
      if (val && !val.startsWith('zodiacSigns.')) return val;
    } catch (_) {}
    return signKey;
  };

  const getHouseText = (houseNum: number): string => {
    return t('houseFormat', { house: houseNum });
  };

  const getMotionStatus = (isRetrograde: boolean): string => {
    try {
      const val = isRetrograde ? tGlossary('planetaryStates.retrograde') : tGlossary('planetaryStates.direct');
      if (val && !val.startsWith('planetaryStates.')) return val;
    } catch (_) {}
    return isRetrograde ? 'Retrograde' : 'Direct';
  };

  const formatTargetDate = (dateStr?: string | null): string => {
    if (!dateStr) return '';
    try {
      return formatLocalizedDate(dateStr, locale);
    } catch (_) {
      return dateStr;
    }
  };

  return (
    <div className="space-y-8" data-testid="transit-chart-client">
      {/* Target Transit Date Input Header */}
      <div className="bg-[var(--card)] border border-[var(--line)] rounded-2xl p-6 shadow-xs space-y-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-serif font-medium text-[var(--foreground)] flex items-center gap-2">
              <Orbit className="w-6 h-6 text-[var(--gold)]" />
              <span>{t('title')}</span>
            </h1>
            <p className="text-xs text-[var(--muted)] mt-1">
              {t('subtitle')}
            </p>
          </div>

          <div className="w-full sm:w-auto">
            <label htmlFor="transit-date-input" className="block text-xs font-semibold text-[var(--muted)] uppercase tracking-wider mb-1">
              {t('targetDateLabel')}
            </label>
            <input
              id="transit-date-input"
              type="date"
              value={transitDate}
              onChange={(e) => setTransitDate(e.target.value)}
              className="w-full sm:w-48 bg-[var(--background)] border border-[var(--line)] rounded-md px-3 py-2 text-xs text-[var(--foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--ring)]"
            />
          </div>
        </div>
      </div>

      <QuickCalculatorForm
        title={t('natalInputTitle')}
        subtitle={t('natalInputSubtitle')}
        onCalculate={handleCalculate}
        loading={loading}
      />

      {error && (
        <div data-testid="transit-chart-error-panel" className="bg-rose-500/10 border border-rose-500/30 rounded-xl p-4 text-rose-500 text-sm">
          {error}
        </div>
      )}

      {result && (
        <div data-testid="transit-chart-result-panel" className="space-y-6">
          {/* Header & Conjunction Summary */}
          <div className="bg-[var(--card)] border border-[var(--line)] rounded-2xl p-6 shadow-xs space-y-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-xl font-serif font-medium text-[var(--foreground)] flex items-center gap-2">
                  <Orbit className="w-5 h-5 text-[var(--gold)]" />
                  <span>
                    {t('analysisTitle', { date: formatTargetDate(result.transit_date) })}
                  </span>
                </h2>
                <p className="text-xs text-[var(--muted)] mt-1">
                  {t('natalAscLabel')}{' '}
                  <span className="text-[var(--gold)] font-semibold">
                    {getSignName(result.natal_ascendant_sign)}
                  </span>
                </p>
              </div>
              <div className="bg-[var(--background)] border border-[var(--line)] rounded-xl px-4 py-2 text-right">
                <span className="text-[10px] text-[var(--muted)] uppercase tracking-wider block font-semibold">
                  {t('conjunctionsBadge')}
                </span>
                <span className="text-sm font-bold text-[var(--gold)]">
                  {t('activeCount', {
                    count: result.transit_overlays?.filter(tr => tr.conjunct_natal_planets && tr.conjunct_natal_planets.length > 0).length || 0
                  })}
                </span>
              </div>
            </div>

            {/* Saturn Return Banner if active */}
            {result.is_saturn_return && (
              <div className="p-4 rounded-xl bg-purple-500/10 border border-purple-500/30 text-purple-400 text-xs space-y-1">
                <div className="font-bold text-purple-300 flex items-center gap-1.5 text-sm">
                  <Sparkles className="w-4 h-4 text-purple-400" />
                  <span>{t('saturnReturnTitle')}</span>
                </div>
                <p>
                  {result.saturn_return_details
                    ? t('saturnReturnDesc', {
                        sign: getSignName(result.saturn_return_details.sign_name),
                        house: result.saturn_return_details.natal_house
                      })
                    : t('saturnReturnTitle')}
                </p>
              </div>
            )}
          </div>

          {/* Transiting Planetary Positions Table */}
          <div className="bg-[var(--card)] border border-[var(--line)] rounded-2xl p-6 shadow-xs">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif font-medium text-lg text-[var(--foreground)] flex items-center gap-2">
                <Compass className="w-5 h-5 text-[var(--gold)]" />
                <span>{t('tableTitle')}</span>
              </h3>
              <span className="text-xs text-[var(--muted)] font-mono">{t('navagrahaCount')}</span>
            </div>

            {/* Mobile Swipe Affordance Hint */}
            <div className="sm:hidden flex items-center justify-between text-[10px] text-[var(--gold)] font-mono mb-2 px-1">
              <span>{t('swipeHint')}</span>
            </div>

            <div className="overflow-x-auto relative rounded-xl border border-[var(--line)]">
              <table className="w-full text-left text-xs text-[var(--muted)] min-w-[640px]">
                <thead className="bg-[var(--background)] text-[var(--muted)] uppercase font-semibold border-b border-[var(--line)]">
                  <tr>
                    <th className="py-3 px-4">{t('colPlanet')}</th>
                    <th className="py-3 px-4">{t('colSign')}</th>
                    <th className="py-3 px-4">{t('colDegree')}</th>
                    <th className="py-3 px-4">{t('colHouse')}</th>
                    <th className="py-3 px-4">{t('colStatus')}</th>
                    <th className="py-3 px-4">{t('colConjunctions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--line)] font-medium">
                  {result.transit_overlays.map((tr) => (
                    <tr key={tr.planet} className="hover:bg-[var(--gold)]/5 transition-colors">
                      <td className="py-3.5 px-4 font-bold text-[var(--foreground)]">
                        {getPlanetName(tr.planet)}
                      </td>
                      <td className="py-3.5 px-4 text-[var(--gold)]">
                        {getSignName(tr.sign_name)}
                      </td>
                      <td className="py-3.5 px-4 font-mono text-[var(--foreground)]">
                        {tr.formatted_degree}
                      </td>
                      <td className="py-3.5 px-4 text-[var(--foreground)]">
                        {getHouseText(tr.natal_house)}
                      </td>
                      <td className="py-3.5 px-4">
                        {tr.is_retrograde ? (
                          <span className="bg-purple-500/20 text-purple-400 border border-purple-500/30 px-2 py-0.5 rounded text-[10px] font-bold">
                            {getMotionStatus(true)}
                          </span>
                        ) : (
                          <span className="text-[var(--muted)] text-[10px]">
                            {getMotionStatus(false)}
                          </span>
                        )}
                      </td>
                      <td className="py-3.5 px-4">
                        {tr.conjunct_natal_planets && tr.conjunct_natal_planets.length > 0 ? (
                          <span className="text-[var(--gold)] font-semibold bg-[var(--gold)]/10 border border-[var(--gold)]/30 px-2 py-0.5 rounded text-[11px]">
                            {t('conjunctFormat', {
                              planets: tr.conjunct_natal_planets.map(p => getPlanetName(p)).join(', ')
                            })}
                          </span>
                        ) : (
                          <span className="text-[var(--muted)] text-[11px]">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Same-Sign Conjunction Highlights */}
          {(result as any).notable_conjunctions && (result as any).notable_conjunctions.length > 0 && (
            <div className="bg-[var(--card)] border border-[var(--line)] rounded-2xl p-6 shadow-xs space-y-3">
              <h3 className="font-serif font-medium text-base text-[var(--foreground)] flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[var(--gold)]" />
                <span>{t('notableHeading')}</span>
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(result as any).notable_conjunctions.map((c: any, idx: number) => (
                  <div key={idx} className="p-3.5 rounded-xl bg-[var(--background)] border border-[var(--line)] space-y-1">
                    <div className="text-xs font-bold text-[var(--gold)]">
                      {t('conjunctionCard', {
                        tPlanet: getPlanetName(c.transiting_planet),
                        nPlanet: getPlanetName(c.natal_planet)
                      })}
                    </div>
                    <div className="text-[11px] text-[var(--muted)]">
                      {t('conjunctionMeta', {
                        sign: getSignName(c.sign_name),
                        house: c.natal_house
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Scope Disclaimer */}
          <p className="text-[11px] text-[var(--muted)] italic px-2">
            {t('scopeDisclaimer')}
          </p>
        </div>
      )}
    </div>
  );
}
